import React from 'react';
import _ from 'underscore';

import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SequenceSchemaType, TagSchemaSupportedFolderItemType} from 'constants/RegistryConstants';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import FormGroup from 'ui-library/bs/FormGroup';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

export interface EntityTypeOption {
  folderItemType: TagSchemaSupportedFolderItemType;
  sequenceType: SequenceSchemaType | null;
}

export interface EntityOptionConfiguration {
  includeAll?: boolean;
  includeMixed?: boolean;
  includeMixture?: boolean;
}

interface TagSchemaEntityTypeSelectorProps {
  disabled?: boolean;
  displayName: string;
  entityOptionConfiguration?: EntityOptionConfiguration;
  onChangeEntityType: (entityType: EntityTypeOption | null) => void;
  selectedEntityType: EntityTypeOption | null;
  showLabel?: boolean;
  width?: MenuWidth;
}

function getItemTypeOptions(
  options?: EntityOptionConfiguration
): Array<{value: EntityTypeOption | null; name: string}> {
  const supportedItemTypes = _.compact([
    {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null},
    {
      folderItemType: ModelTypeConstants.SEQUENCE,
      sequenceType: SequenceSchemaType.DNA_SEQUENCE,
    },
    {
      folderItemType: ModelTypeConstants.SEQUENCE,
      sequenceType: SequenceSchemaType.OLIGO,
    },
    {
      folderItemType: ModelTypeConstants.SEQUENCE,
      sequenceType: SequenceSchemaType.RNA_OLIGO,
    },
    {
      folderItemType: ModelTypeConstants.SEQUENCE,
      sequenceType: SequenceSchemaType.RNA_SEQUENCE,
    },
    options?.includeMixed && {
      folderItemType: ModelTypeConstants.SEQUENCE,
      sequenceType: SequenceSchemaType.MIXED,
    },
    {folderItemType: ModelTypeConstants.PROTEIN, sequenceType: null},
    BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE && {
      folderItemType: ModelTypeConstants.MOLECULE,
      sequenceType: null,
    },
    BenchlingConfig.ENABLE_ENTRY_SCHEMAS && {folderItemType: ModelTypeConstants.ENTRY, sequenceType: null},
    (isFeatureEnabled({feature: RegistryFeature.REGISTRY_MIXTURES}) || options?.includeMixture) && {
      folderItemType: ModelTypeConstants.MIXTURE,
      sequenceType: null,
    },
  ]);

  const itemTypes = supportedItemTypes.map((type) => {
    const folderItemType = type.folderItemType as TagSchemaSupportedFolderItemType;
    return {
      value: {folderItemType, sequenceType: type.sequenceType},
      name: TagSchemaUtil.getTagSchemaTypeName({schema: {folderItemType, sequenceType: type.sequenceType}}),
    };
  });

  return options?.includeAll ? [{value: null, name: 'All'}, ...itemTypes] : itemTypes;
}

export default function TagSchemaEntityTypeSelector(props: TagSchemaEntityTypeSelectorProps): JSX.Element {
  const entityOptions = getItemTypeOptions(props.entityOptionConfiguration);

  function renderSelectDropdown(): JSX.Element {
    return (
      <SelectDropdown
        displayName={props.displayName}
        menu={
          <SearchableMenu
            onSelect={props.onChangeEntityType}
            selectedValue={props.selectedEntityType}
            width={props.width ?? Menu.WIDTHS.FULL}
            optionDatas={entityOptions}
          />
        }
        disabled={props.disabled}
      />
    );
  }

  return props.showLabel ? (
    <FormGroup>
      <FieldLabel label="Entity type" />
      {renderSelectDropdown()}
    </FormGroup>
  ) : (
    renderSelectDropdown()
  );
}
